/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { useEffect, useRef } from 'react';

// Define types for WebLocks API
interface LockGrantedCallback {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (lock: { name: string; [key: string]: any }): Promise<any> | void;
}

interface LockOptions {
  mode?: 'shared' | 'exclusive';
  ifAvailable?: boolean;
  steal?: boolean;
  signal?: AbortSignal;
}

interface WebLockManager {
  request(name: string, callback: LockGrantedCallback): Promise<any>;
  request(name: string, options: LockOptions, callback: LockGrantedCallback): Promise<any>;
  query(): Promise<{ held: any[]; pending: any[] }>;
}

/**
 * Hook to help components handle Back/Forward cache (BFCache) events.
 * 
 * @param onRestore Function to call when the page is restored from BFCache
 * @param onVisibilityChange Optional function to call when visibility state changes
 * @returns An object with refs that can be used to manage resources
 */
export function useBfcacheSupport(
  onRestore?: () => void,
  onVisibilityChange?: (isVisible: boolean) => void
) {
  const isRestoredRef = useRef(false);
  const isVisibleRef = useRef(true);
  
  // Handle page visibility changes
  useEffect(() => {
    // Set initial state
    isVisibleRef.current = document.visibilityState === 'visible';
    
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      isVisibleRef.current = isVisible;
      
      if (onVisibilityChange) {
        onVisibilityChange(isVisible);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVisibilityChange]);
  
  // Handle BFCache restoration
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        isRestoredRef.current = true;
        
        if (onRestore) {
          onRestore();
        }
      }
    };
    
    // Also listen for custom event from layout.tsx
    const handleBfcacheRestore = () => {
      isRestoredRef.current = true;
      
      if (onRestore) {
        onRestore();
      }
    };
    
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('bfcacheRestore', handleBfcacheRestore);
    
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('bfcacheRestore', handleBfcacheRestore);
    };
  }, [onRestore]);
  
  return {
    isRestoredRef,
    isVisibleRef
  };
}

/**
 * Utility to add noopener to external links
 * 
 * @param url The URL to check
 * @returns An object with the rel attribute to add to external links
 */
export function getExternalLinkAttributes(url?: string) {
  if (!url) return {};
  
  // Check if URL is external (starts with http or https)
  const isExternal = url.startsWith('http://') || url.startsWith('https://');
  
  if (isExternal) {
    return {
      rel: 'noopener',
      target: '_blank'
    };
  }
  
  return {};
}

/**
 * Checks if Web Locks API is available and supported in the current browser.
 * Web Locks API is known to prevent bfcache in some browsers.
 */
export function isWebLocksSupported(): boolean {
  return typeof navigator !== 'undefined' && 
         'locks' in navigator && 
         typeof navigator.locks !== 'undefined';
}

/**
 * Type augmentation for Navigator to include WebLocks API
 */
declare global {
  interface Navigator {
    locks?: WebLockManager;
  }
  
  interface Window {
    __restoreOriginalWebLocks?: () => void;
    __webLocksPolyfilled?: boolean;
  }
}

/**
 * Creates a polyfill for Web Locks API that doesn't actually use locks,
 * allowing pages to work with bfcache. Useful for third-party scripts
 * that might use Web Locks API.
 * 
 * IMPORTANT: Only use this in contexts where locks aren't critical for functionality.
 */
export function polyfillWebLocksForBfcache(): void {
  // Check if polyfill is already applied
  if (typeof window !== 'undefined' && window.__webLocksPolyfilled) {
    return;
  }

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    // Save original implementation if it exists
    const originalLocks = navigator.locks;
    
    // Replace with dummy implementation that doesn't block bfcache
    Object.defineProperty(navigator, 'locks', {
      value: {
        request: (name: string, optionsOrCallback: LockOptions | LockGrantedCallback, maybeCallback?: LockGrantedCallback): Promise<unknown> => {
          // Handle both function signatures
          const callback = typeof optionsOrCallback === 'function' 
            ? optionsOrCallback 
            : maybeCallback;
          
          // Just execute the callback immediately without actual locking
          if (typeof callback === 'function') {
            return Promise.resolve(callback({ name }));
          }
          return Promise.resolve(undefined);
        },
        query: async () => {
          return { held: [], pending: [] };
        }
      },
      configurable: true
    });
    
    // Mark as polyfilled
    if (typeof window !== 'undefined') {
      window.__webLocksPolyfilled = true;
    }
    
    // Add restoration function to window object for debugging
    window.__restoreOriginalWebLocks = () => {
      Object.defineProperty(navigator, 'locks', {
        value: originalLocks,
        configurable: true
      });
      
      if (typeof window !== 'undefined') {
        window.__webLocksPolyfilled = false;
      }
    };
  }
} 